import React from 'react';

const styles = {
  tableContainer: {
    width: '100%',
    overflowX: 'auto',
    backgroundColor: 'var(--bg-elevated)',
    borderRadius: 'var(--radius-xl)',
    boxShadow: 'var(--shadow-lg)',
    border: '1px solid var(--border-light)',
    position: 'relative',
    WebkitOverflowScrolling: 'touch',
  },
  luxuryAccent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'var(--gradient-gold)',
  },
  table: {
    width: '100%',
    minWidth: '600px',
    borderCollapse: 'separate',
    borderSpacing: '0',
    tableLayout: 'auto',
  },
  thead: {
    backgroundColor: 'var(--bg-secondary)',
    position: 'relative',
  },
  th: {
    padding: 'var(--space-3) var(--space-3)',
    textAlign: 'left',
    fontSize: 'var(--text-xs)',
    fontFamily: 'var(--font-body)',
    fontWeight: '700',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: 'var(--tracking-widest)',
    borderBottom: '2px solid var(--gold)',
    whiteSpace: 'nowrap',
    position: 'sticky',
    backgroundColor: 'var(--bg-secondary)',
  },
  thFirst: {
    borderTopLeftRadius: 'var(--radius-xl)',
  },
  thLast: {
    borderTopRightRadius: 'var(--radius-xl)',
  },
  td: {
    padding: 'var(--space-4) var(--space-3)',
    fontSize: 'var(--text-sm)',
    fontFamily: 'var(--font-body)',
    color: 'var(--text)',
    borderBottom: '1px solid var(--border-light)',
    transition: 'all 0.2s ease',
  },
  tr: {
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  trHover: {
    backgroundColor: 'var(--gold-subtle)',
  },
  trClickable: {
    cursor: 'pointer',
  },
  trLast: {
    borderBottom: 'none',
  },
  emptyState: {
    padding: 'var(--space-16) var(--space-4)',
    textAlign: 'center',
    color: 'var(--text-tertiary)',
    fontSize: 'var(--text-base)',
    fontFamily: 'var(--font-body)',
    fontStyle: 'italic',
  },
  actions: {
    display: 'flex',
    gap: 'var(--space-3)',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  cellHighlight: {
    fontWeight: 600,
    color: 'var(--gold-dark)',
  },
};

export function Table({ 
  columns, 
  data, 
  emptyMessage = 'No hay datos disponibles',
  hoverable = true,
  onRowClick,
  luxuryAccent = true,
  highlightColumns = [],
  rowStyle,
  className = '',
  ...props 
}) {
  const [hoveredRow, setHoveredRow] = React.useState(null);

  return (
    <div style={styles.tableContainer} className={`luxury-table ${className}`} {...props}>
      {luxuryAccent && <div style={styles.luxuryAccent} />}
      
      <table style={styles.table}>
        <thead style={styles.thead}>
          <tr>
            {columns.map((column, index) => (
              <th 
                key={column.key} 
                style={{
                  ...styles.th,
                  ...(index === 0 ? styles.thFirst : {}),
                  ...(index === columns.length - 1 ? styles.thLast : {}),
                  ...(column.width ? { width: column.width } : {}),
                  ...(column.align ? { textAlign: column.align } : {}),
                }}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} style={styles.emptyState}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, rowIndex) => (
              <tr
                key={row._id || rowIndex}
                style={{
                  ...styles.tr,
                  ...(hoverable && hoveredRow === rowIndex ? styles.trHover : {}),
                  ...(onRowClick ? styles.trClickable : {}),
                  ...(rowStyle ? rowStyle(row) : {}),
                }}
                onMouseEnter={() => hoverable && setHoveredRow(rowIndex)}
                onMouseLeave={() => hoverable && setHoveredRow(null)}
                onClick={() => onRowClick && onRowClick(row)}
              >
                {columns.map((column) => (
                  <td 
                    key={column.key} 
                    style={{
                      ...styles.td,
                      ...(rowIndex === data.length - 1 ? { borderBottom: 'none' } : {}),
                      ...(highlightColumns.includes(column.key) ? styles.cellHighlight : {}),
                      ...(column.align ? { textAlign: column.align } : {}),
                    }}
                  >
                    {column.render ? column.render(row[column.key], row) : row[column.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default Table;